'use client';

import Link from 'next/link';
import Image from 'next/image';
import React, { useEffect, useState, useRef } from 'react';
import InterestSection from '@/components/InterestSection';
import ArrowIcon from '@/components/ArrowIcon';

interface Project {
  id: string;
  title: string;
  projectUrl: string;
  imageUrl: string;
  tags: string;
}

interface Service {
  id: string;
  slug: string;
  title: string;
  heading: string;
  description: string;
  imageUrl: string | null;
  videoUrl: string | null;
}

interface WhyBox {
  id: string;
  title: string;
  description: string;
}

interface ProcessStep {
  id: string;
  number: number;
  title: string;
  description: string;
  imageUrl: string | null;
}

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

interface HeaderLines {
  line1: string;
  line2: string;
  line3: string;
}

interface HomeContentProps {
  projects: Project[];
  services: Service[];
  whyBoxes: WhyBox[];
  processSteps: ProcessStep[];
  faqItems: FaqItem[];
  headerLines: HeaderLines;
  headerSubtitle: string;
  heroMockupUrl: string | null;
}

export default function HomeContent({ projects, services, whyBoxes, processSteps, faqItems, headerLines, headerSubtitle, heroMockupUrl }: HomeContentProps) {

  useEffect(() => {
    // Projects section shrink effect
    const handleScroll = () => {
      const servicesSection = document.querySelector('#projects');
      if (servicesSection) {
        const sectionBottom = (servicesSection as HTMLElement).offsetTop + (servicesSection as HTMLElement).offsetHeight;
        const viewportBottom = window.scrollY + window.innerHeight;

        if (viewportBottom >= sectionBottom) {
          servicesSection.classList.add('shrink');
        } else {
          servicesSection.classList.remove('shrink');
        }
      }
    };

    document.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Get featured projects (first 6)
  const featuredProjects = projects.slice(0, 6);

  return (
    <>
      <header id="about">
        {/* Old effects — hidden via CSS, can be re-enabled */}
        <div className="grid"></div>
        <div className="aurora"></div>
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>

        {/* Geometric decorations */}
        <div className="geo-shapes" aria-hidden="true">
          <svg className="geo-ring" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            <circle cx="100" cy="100" r="60" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
          </svg>
          <svg className="geo-cross" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="50" y1="0" x2="50" y2="100" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <line x1="0" y1="50" x2="100" y2="50" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
          </svg>
          <svg className="geo-dots" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0, 30, 60, 90].map(x => [0, 30, 60, 90].map(y => (
              <circle key={`${x}-${y}`} cx={x + 15} cy={y + 15} r="2" fill="rgba(255,255,255,0.2)" />
            )))}
          </svg>
          <div className="geo-line-diagonal"></div>
          {/* Left-side shapes */}
          <svg className="geo-ring-left" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="90" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
            <circle cx="100" cy="100" r="65" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <circle cx="100" cy="100" r="40" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
          </svg>
          <svg className="geo-triangle" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polygon points="50,5 95,95 5,95" stroke="rgba(255,255,255,0.18)" strokeWidth="1" fill="none" />
          </svg>
          <div className="geo-line-left"></div>
          <svg className="geo-dots-left" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            {[0, 25, 50, 75].map(x => [0, 25, 50, 75].map(y => (
              <circle key={`l-${x}-${y}`} cx={x + 12} cy={y + 12} r="1.5" fill="rgba(255,255,255,0.2)" />
            )))}
          </svg>
          <svg className="geo-diamond" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="29" y="29" width="42" height="42" stroke="rgba(255,255,255,0.15)" strokeWidth="1" fill="none" transform="rotate(45 50 50)" />
          </svg>
        </div>

        <div className="header-content big">
          <div className="header-left">
            <h1 className="header-animate">
              <span className="anim-item">{headerLines.line1}</span>
              <span className="anim-item highlight">{headerLines.line2}</span>
              <span className="anim-item">{headerLines.line3}</span>
            </h1>
            <p className="header-subtitle anim-item">{headerSubtitle}</p>
            <div className="header-cta anim-item">
              <Link href="/kontakt" className="btn-white">
                Získat návrh řešení zdarma
              </Link>
              <Link href="/portfolio" className="btn-text">
                Ukázky mé práce
              </Link>
            </div>
          </div>
          <div className="header-mockup anim-item">
            <Image
              src="/images/hero/header1.png"
              alt="Ukázka práce"
              width={900}
              height={600}
              priority
              sizes="(max-width: 768px) 100vw, 65vw"
            />
          </div>
        </div>
        <div className="c-scrolldown">
          <div className="c-line"></div>
        </div>
      </header>

      <section className="main-content">
        <div className="services-alt" id="services">
          {services.map((service, index) => (
            <div key={service.id} className={`service-row ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="service-text">
                <h2>{service.title}</h2>
                <p dangerouslySetInnerHTML={{ __html: service.description.replace(/\n/g, '<br />') }} />
                <Link href="/kontakt" className="service-btn">
                  Poptat řešení
                </Link>
              </div>
              <ServiceMedia
                imageUrl={service.imageUrl || `/images/services/${service.slug}.jpg`}
                videoUrl={service.videoUrl}
                title={service.title}
              />
            </div>
          ))}
        </div>

        <div className="why" id="why">
          <div className="why-content">
            {whyBoxes.map((box) => (
              <div key={box.id} className="why-box">
                <div className="info">
                  <CountUpNumber value={box.title} />
                  <p>{box.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="services" id="projects">
          <div className="portfolio-header">
            <span className="portfolio-label">Ukázky práce</span>
            <h2>Weby, které přivádějí zákazníky</h2>
            <p>Podívejte se, jak jsem pomohl firmám růst díky webům, které opravdu fungují.</p>
          </div>
          <div className="portfolio-content">
            {featuredProjects.map((project) => {
              const tags = JSON.parse(project.tags) as string[];
              return (
                <PortfolioItem
                  key={project.id}
                  href={project.projectUrl}
                  image={project.imageUrl}
                  title={project.title}
                  tags={tags}
                />
              );
            })}
            <div className="portfolio-button">
              <Link href="/portfolio" className="btn w-arrow light" aria-label="zobrazit projekty">
                <span>Další realizace</span>
                <div className="arrows" aria-hidden="true">
                  <ArrowIcon />
                  <ArrowIcon />
                </div>
              </Link>
              <Link href="/cenik" className="btn w-arrow light" aria-label="zobrazit ceník">
                <span>Zjistit cenu</span>
                <div className="arrows" aria-hidden="true">
                  <ArrowIcon />
                  <ArrowIcon />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {processSteps.length > 0 && (
        <section className="process">
          <div className="process-content">
            <div className="process-header">
              <span className="process-label">Jak spolupracujeme</span>
              <h2>Od poptávky ke spuštění za 4 kroky</h2>
              <p>Žádné zbytečné schůzky. Jasný postup, ve kterém víte, co se děje a kdy to bude.</p>
            </div>
            <div className="process-grid">
              {processSteps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="process-step">
                    <div className="step-top">
                      <span className="step-label">Krok {String(step.number).padStart(2, '0')}</span>
                      <h3>{step.title}</h3>
                    </div>
                    {step.imageUrl && (
                      <div className="step-image">
                        <Image
                          src={step.imageUrl}
                          alt={step.title}
                          width={400}
                          height={250}
                          sizes="(max-width: 768px) 90vw, 280px"
                        />
                      </div>
                    )}
                    <p>{step.description}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="process-arrow" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>
      )}

      {faqItems.length > 0 && (
        <section className="faq">
          <div className="faq-content">
            <div className="faq-header">
              <span className="faq-label">Chcete vědět víc?</span>
              <h2>Na co se klienti ptají nejčastěji</h2>
              <p>Odpovědi na otázky, které řeší většina firem před startem projektu.</p>
            </div>
            <div className="faq-list">
              {faqItems.map((item) => (
                <FaqAccordionItem key={item.id} question={item.question} answer={item.answer} />
              ))}
            </div>
          </div>
        </section>
      )}

      <InterestSection />
    </>
  );
}

function FaqAccordionItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button
        className={`faq-question ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        aria-expanded={isOpen}
      >
        <h3>{question}</h3>
        <svg
          className="faq-icon"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`} role="region">
        <p>{answer}</p>
      </div>
    </div>
  );
}

function CountUpNumber({ value }: { value: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [displayed, setDisplayed] = useState(value);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Parse number and suffix from value like "250+" or "12+"
    const match = value.match(/^(\d+)(.*)$/);
    if (!match) return; // Not a number, just display as-is

    const target = parseInt(match[1], 10);
    const suffix = match[2]; // e.g. "+"

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 1800;
          const start = performance.now();

          const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(eased * target);
            setDisplayed(`${current}${suffix}`);
            if (progress < 1) requestAnimationFrame(animate);
          };

          setDisplayed(`0${suffix}`);
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return <p ref={ref} className="why-number">{displayed}</p>;
}

function ServiceMedia({ imageUrl, videoUrl, title }: { imageUrl: string; videoUrl: string | null; title: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!videoUrl || !videoRef.current || !containerRef.current) return;

    const video = videoRef.current;
    const container = containerRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
          setIsPlaying(true);
        } else {
          video.pause();
          video.currentTime = 0;
          setIsPlaying(false);
        }
      },
      { threshold: 0.4 },
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [videoUrl]);

  return (
    <div className="service-image" ref={containerRef}>
      <Image
        src={imageUrl}
        alt={title}
        width={600}
        height={600}
        sizes="(max-width: 768px) 100vw, 50vw"
        className={videoUrl ? `service-thumbnail${isPlaying ? ' hidden' : ''}` : ''}
      />
      {videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          muted
          loop
          playsInline
          preload="none"
          className={`service-video${isPlaying ? ' visible' : ''}`}
        />
      )}
    </div>
  );
}

interface PortfolioItemProps {
  href: string;
  image: string;
  title: string;
  tags: string[];
}

function PortfolioItem({ href, image, title, tags }: PortfolioItemProps) {
  return (
    <div className="portfolio-box">
      <a target="_blank" href={href} className="link" rel="noopener noreferrer">
        <div className="portfolio-box-inner">
          <Image src={image} alt={`${title} - ukázka webu`} width={800} height={600} sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
          <div className="overlay"></div>
          <div className="overlay2"></div>
          <div className="info">
            <h3>{title}</h3>
            <ul>
              {tags.map((tag, index) => (
                <li key={index}>{tag}</li>
              ))}
            </ul>
            <Image className="arrow" src="/images/icons/icons8-right-arrow-32.png" alt="" width={32} height={32} aria-hidden="true" />
          </div>
        </div>
      </a>
    </div>
  );
}
